import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { LifeBuoy, ShieldAlert } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { fetchMyTasks, type MyTasks as MyTasksData } from '@/lib/adapters'
import { ErrorState, Skeleton } from '@/components/States'

/**
 * A2.09 — «Mening vazifalarim» (shaxsiy navbat). Joriy xodimning ish-ro'yxati:
 * unga tayinlangan ochiq tiketlar + uning tasdig'ini kutayotgan four-eyes
 * so'rovlari. `/admin/me` + `/admin/tickets` + `/admin/four-eyes` ustidan JONLI
 * filtrlanadi — alohida backend KUTMAYDI. Xodimning kunlik boshlang'ich ekrani.
 */
export function MyTasks() {
  const t = useT()
  const [data, setData] = useState<MyTasksData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchMyTasks(ac.signal)
      .then((d) => {
        if (alive) setData(d)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])

  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />

  const greeting = data?.me ? `@${data.me.username}` : ''

  return (
    <>
      <PageHeader
        title={t.myTasks.title}
        screenId="A2.09"
        description={greeting ? `${t.myTasks.note} — ${greeting}` : t.myTasks.note}
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
          <KPI label={t.myTasks.myTickets} value={data?.my_tickets.length} loading={loading} warn={(data?.my_tickets.length ?? 0) > 0} />
          <KPI label={t.myTasks.approvals} value={data?.approvals.length} loading={loading} warn={(data?.approvals.length ?? 0) > 0} />
        </div>

        <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
          <Panel title={t.myTasks.myTickets} icon={<LifeBuoy size={15} />}>
            {loading ? (
              <ListSkeleton />
            ) : !data || !data.my_tickets.length ? (
              <Empty t={t.myTasks.noTickets} />
            ) : (
              <div className="divide-y divide-border-subtle">
                {data.my_tickets.map((tk) => (
                  <Link
                    key={tk.id}
                    to={`/support/${tk.id}`}
                    className="flex items-center justify-between gap-2 py-1.5 text-sm hover:bg-surface-3"
                  >
                    <span className="min-w-0 flex-1 truncate text-primary">{tk.subject || `#${tk.id}`}</span>
                    <span className="shrink-0 text-tertiary">{tk.category}</span>
                    <span className="w-16 shrink-0 text-right text-xs text-secondary">{tk.status}</span>
                  </Link>
                ))}
              </div>
            )}
          </Panel>

          <Panel title={t.myTasks.approvals} icon={<ShieldAlert size={15} />}>
            {loading ? (
              <ListSkeleton />
            ) : !data || !data.approvals.length ? (
              <Empty t={t.myTasks.noApprovals} />
            ) : (
              <div className="divide-y divide-border-subtle">
                {data.approvals.map((r) => (
                  <Link
                    key={r.id}
                    to="/security/four-eyes"
                    className="flex items-center justify-between gap-2 py-1.5 text-sm hover:bg-surface-3"
                  >
                    <span className="min-w-0 flex-1 truncate">
                      <span className="font-mono text-xs text-accent">{r.action}</span>
                      {r.target && <span className="ml-1.5 text-tertiary">{r.target}</span>}
                    </span>
                    <span className="shrink-0 text-xs text-tertiary">{r.requester}</span>
                    <span
                      className={`w-8 shrink-0 text-right font-mono text-xs ${
                        r.danger_level >= 5 ? 'text-danger' : 'text-warning'
                      }`}
                    >
                      L{r.danger_level}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </Panel>
        </div>
      </div>
    </>
  )
}

function KPI({
  label,
  value,
  loading,
  warn,
}: {
  label: string
  value: number | undefined
  loading: boolean
  warn?: boolean
}) {
  return (
    <div className="rounded-lg border border-border-default bg-surface-2 p-3">
      <div className="text-xs text-tertiary">{label}</div>
      {loading ? (
        <Skeleton className="mt-1.5 h-7 w-16" />
      ) : (
        <div className={`mt-0.5 font-mono text-2xl tabular ${warn ? 'text-warning' : 'text-primary'}`}>
          {value?.toLocaleString() ?? '—'}
        </div>
      )}
    </div>
  )
}

function Panel({ title, icon, children }: { title: string; icon: ReactNode; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-border-default bg-surface-2 p-3">
      <div className="mb-2 flex items-center gap-1.5 text-sm font-medium text-secondary">
        <span className="text-tertiary">{icon}</span>
        {title}
      </div>
      {children}
    </div>
  )
}

function ListSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 5 }, (_, i) => (
        <Skeleton key={i} className="h-5 w-full" />
      ))}
    </div>
  )
}

function Empty({ t }: { t: string }) {
  return <div className="py-6 text-center text-sm text-tertiary">{t}</div>
}
